import { Extension } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
import { getBlockIcon } from "../blockIcons";

const GROUP_LABELS = {
  style: "Стиль",
  insert: "Вставка",
};

const defaultItems = [
  {
    title: "Параграф",
    group: "style",
    icon: "paragraph",
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setParagraph().run(),
  },
  {
    title: "Заголовок 1",
    group: "style",
    icon: "h1",
    command: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleHeading({ level: 1 })
        .run(),
  },
  {
    title: "Заголовок 2",
    group: "style",
    icon: "h2",
    command: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleHeading({ level: 2 })
        .run(),
  },
  {
    title: "Заголовок 3",
    group: "style",
    icon: "h3",
    command: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleHeading({ level: 3 })
        .run(),
  },
  {
    title: "Маркированный список",
    group: "style",
    icon: "bullet-list",
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    title: "Нумерованный список",
    group: "style",
    icon: "ordered-list",
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
  {
    title: "Цитата",
    group: "style",
    icon: "quote",
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
  },
  {
    title: "Блок кода",
    group: "style",
    icon: "code",
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
  },
  {
    title: "Таблица",
    group: "insert",
    icon: "table",
    command: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 2, cols: 2, withHeaderRow: false })
        .run(),
  },
  {
    title: "Разделитель",
    group: "insert",
    icon: "divider",
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
  },
];

const SlashCommands = Extension.create({
  name: "slashCommands",

  addOptions() {
    return {
      suggestion: {
        char: "/",
        startOfLine: false,
        items: ({ query }) => {
          if (!query) return defaultItems;
          const q = query.toLowerCase();
          return defaultItems.filter((item) =>
            item.title.toLowerCase().includes(q)
          );
        },
        command: ({ editor, range, props }) => {
          props.command({ editor, range });
        },
        render: () => {
          let popup;
          let selectedIndex = 0;
          let currentItems = [];

          const createItem = (item, command) => {
            const el = document.createElement("div");
            el.className = "slash-command-item";

            const iconEl = document.createElement("div");
            iconEl.className = "slash-command-item-icon";
            iconEl.innerHTML = getBlockIcon(item.icon);

            const titleEl = document.createElement("div");
            titleEl.className = "slash-command-item-title";
            titleEl.textContent = item.title;

            el.appendChild(iconEl);
            el.appendChild(titleEl);

            el.addEventListener("click", () => command(item));

            return el;
          };

          const renderList = (popupEl, items, command) => {
            popupEl.innerHTML = "";
            let currentGroup = null;

            items.forEach((item) => {
              const group = item.group || "style";
              if (group !== currentGroup) {
                if (currentGroup !== null) {
                  const divider = document.createElement("div");
                  divider.className = "slash-command-divider";
                  popupEl.appendChild(divider);
                }
                currentGroup = group;
                const label = document.createElement("div");
                label.className = "slash-command-label";
                label.textContent = GROUP_LABELS[group] || group;
                popupEl.appendChild(label);
              }
              popupEl.appendChild(createItem(item, command));
            });

            refreshActive(popupEl);
          };

          const updatePosition = (popupEl, clientRect, editor) => {
            let rect = clientRect?.();

            // Fallback: use cursor position if clientRect is missing
            if (!rect && editor) {
              const { from } = editor.state.selection;
              const coords = editor.view.coordsAtPos(from);
              rect = {
                bottom: coords.bottom,
                left: coords.left,
              };
            }

            if (!rect) return;
            popupEl.style.top = `${rect.bottom + window.scrollY + 4}px`;
            popupEl.style.left = `${rect.left + window.scrollX}px`;
          };

          const refreshActive = (popupEl) => {
            const itemEls = popupEl.querySelectorAll(".slash-command-item");
            itemEls.forEach((el, i) => {
              el.classList.toggle("is-active", i === selectedIndex);
            });

            const activeEl = itemEls[selectedIndex];
            if (activeEl) {
              activeEl.scrollIntoView({ block: "nearest" });
            }
          };

          return {
            onStart: (props) => {
              currentItems = props.items;
              selectedIndex = 0;

              popup = document.createElement("div");
              popup.className = "slash-command-popup";

              renderList(popup, currentItems, props.command);

              document.body.appendChild(popup);
              updatePosition(popup, props.clientRect, props.editor);
            },

            onUpdate: (props) => {
              currentItems = props.items;
              selectedIndex = 0;

              renderList(popup, currentItems, props.command);
              updatePosition(popup, props.clientRect, props.editor);
            },

            onKeyDown: (props) => {
              if (!popup) return false;

              if (props.event.key === "ArrowDown") {
                selectedIndex = (selectedIndex + 1) % currentItems.length;
                refreshActive(popup);
                return true;
              }

              if (props.event.key === "ArrowUp") {
                selectedIndex =
                  (selectedIndex - 1 + currentItems.length) %
                  currentItems.length;
                refreshActive(popup);
                return true;
              }

              if (props.event.key === "Enter") {
                if (currentItems[selectedIndex]) {
                  props.command(currentItems[selectedIndex]);
                }
                return true;
              }

              if (props.event.key === "Escape") {
                popup.remove();
                popup = null;
                return true;
              }

              return false;
            },

            onExit: () => {
              popup?.remove();
              popup = null;
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

export default SlashCommands;
